/* global FavoritesEnabled */
import React from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { PageNames, SegmentSources, tealiumEventPageTypes } from '@foxcorp/delta-web-analytics';
import { isMovie, isSeries, isSpecial, isSports } from 'helpers/ContentHelper';
import { renderImage } from 'helpers/ImageHelper';
import { FORMAT_TIME, stringToDate, format } from 'helpers/DateTimeHelper';
import Icon from 'components/Icon';
import { formatUpcomingMetadata } from 'helpers/VideoHelper';
import Favorite, { FAVORITE_TYPE_LINK, PLUS } from 'components/Favorite';

import Style from './UpcomingModal.module.scss';

const {
  global_favoriteButton: defaultText,
  global_favoriteSetButton: favoritedText,
} = window.Language;

const UpcomingModal = ({
  closeOverlay,
  contentRating,
  description,
  durationInSeconds,
  favoritableItems,
  favoritesManager,
  images,
  name,
  network,
  networkIcon,
  recommendationId,
  seriesName,
  seriesType,
  showCode,
  sportTag,
  startDate,
  subRatings,
  titleLink,
  videoType,
}) => {
  const showImage = _.get(images, 'videoList.raw', false) || _.get(images, 'seriesList.raw', '');

  const type = () => {
    if (isSeries(seriesType) || isSpecial(seriesType)) return 'Show';
    if (isMovie(videoType)) return 'Movie';
    return '';
  };

  const renderNetworkInfo = () => {
    const time = format(stringToDate(startDate), FORMAT_TIME);
    return (
      <div className={Style.networkInfo}>
        {renderImage({
          alt: network,
          src: networkIcon,
        })}
        {time}
      </div>
    );
  };

  const renderSubtitle = () => {
    const subtitle = () => {
      if (isMovie(videoType) || isSpecial(seriesType)) return null;
      let subtitleName = name;
      if (subtitleName === seriesName) return null;
      if (isSports(seriesType) && sportTag) subtitleName = sportTag;
      return subtitleName;
    };
    const itemSubtitle = subtitle();
    return itemSubtitle && <div className={Style.subtitle}>{itemSubtitle}</div>;
  };

  const renderMetadata = () => {
    const metadata = formatUpcomingMetadata({
      startDate,
      contentRating,
      subRatings,
      durationInSeconds,
    });
    return <div className={Style.metadata}>{metadata}</div>;
  };

  const renderSeriesDetailButton = () => {
    if (isSports(seriesType)) return null;
    const showType = type();
    return (
      <Link to={titleLink} onClick={closeOverlay}>
        <button className={Style.seriesDetailButton} type="button">
          <span>{`Go to ${showType} Page`}</span>
        </button>
      </Link>
    );
  };

  const renderFavorite = () => {
    const favorites = _.get(favoritesManager, 'favorites');
    const favoriteData = {
      favoritableItems,
      location: _.get(tealiumEventPageTypes, PageNames.HOME),
      networkId: network,
      recommendationId,
      seriesName,
      showID: showCode,
      source: SegmentSources.DETAIL,
    };
    if (FavoritesEnabled) {
      if (isSports(seriesType)) return null;
      return (
        <div className={Style.favorite}>
          <Favorite
            theme={Style}
            manager={favoritesManager}
            favorites={favorites}
            data={favoriteData}
            type={FAVORITE_TYPE_LINK}
            defaultText={defaultText}
            favoritedText={favoritedText}
            iconType={PLUS}
          />
        </div>
      );
    }
    return null;
  };

  const renderFooter = () => (
    <div className={Style.footer}>
      <button aria-label="close" className={Style.close} onClick={closeOverlay} type="button">
        <Icon size="medium" color="white" icon="close" />
      </button>
    </div>
  );

  return (
    <div className={Style.container}>
      <div className={Style.innerContainer}>
        <img alt={name} src={showImage} />
        {renderNetworkInfo()}
        <div className={Style.title}>{seriesName}</div>
        {renderSubtitle()}
        {renderMetadata()}
        <div className={Style.description}>{description}</div>
        {renderSeriesDetailButton()}
        {renderFavorite()}
        {renderFooter()}
      </div>
    </div>
  );
};

UpcomingModal.propTypes = {
  closeOverlay: PropTypes.func,
  contentRating: PropTypes.string,
  description: PropTypes.string,
  durationInSeconds: PropTypes.number,
  favoritableItems: PropTypes.arrayOf(PropTypes.shape({})),
  favoritesManager: PropTypes.shape({}),
  images: PropTypes.shape([]),
  name: PropTypes.string,
  network: PropTypes.string,
  networkIcon: PropTypes.string,
  recommendationId: PropTypes.string,
  seriesName: PropTypes.string,
  seriesType: PropTypes.string,
  showCode: PropTypes.string,
  sportTag: PropTypes.string,
  startDate: PropTypes.string,
  subRatings: PropTypes.arrayOf(PropTypes.string),
  titleLink: PropTypes.string,
  videoType: PropTypes.string,
};

UpcomingModal.defaultProps = {
  closeOverlay: () => {},
  contentRating: null,
  description: '',
  durationInSeconds: null,
  favoritableItems: [],
  favoritesManager: null,
  images: null,
  name: '',
  network: null,
  networkIcon: '',
  recommendationId: null,
  seriesName: '',
  seriesType: null,
  showCode: '',
  sportTag: null,
  startDate: null,
  subRatings: [],
  titleLink: null,
  videoType: null,
};

export default UpcomingModal;
